import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Edit3,
  Hash,
  MapPin,
  MonitorSmartphone,
  Package,
  Phone,
  UserRound,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { apiFetch } from '@/lib/http';
import { apiRequest } from '@/lib/queryClient';
import type { StoreProfileDetail } from '../../types';

interface StoreDetailsViewProps {
  profile: StoreProfileDetail;
}

interface StoreFormatOption {
  id: number;
  name: string;
  retailerId: number;
}

interface StoreDraft {
  name: string;
  address: string;
  phone: string;
  managerName: string;
  retailerIdentifier: string;
  region: string;
  formatId: string;
  latitude: string;
  longitude: string;
  hasESL: 'true' | 'false';
  notes: string;
}

function draftFromProfile(profile: StoreProfileDetail): StoreDraft {
  return {
    name: profile.storeName,
    address: profile.fullAddress,
    phone: profile.storeDetails.phone ?? '',
    managerName: profile.storeDetails.managerName ?? '',
    retailerIdentifier: profile.storeDetails.retailerIdentifier ?? '',
    region: profile.region === '—' ? '' : profile.region,
    formatId: profile.storeDetails.formatId != null ? String(profile.storeDetails.formatId) : 'none',
    latitude: profile.coordinates?.lat != null ? String(profile.coordinates.lat) : '',
    longitude: profile.coordinates?.lng != null ? String(profile.coordinates.lng) : '',
    hasESL: profile.storeDetails.hasESL ? 'true' : 'false',
    notes: profile.storeDetails.notes ?? '',
  };
}

function DetailItem({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Building2;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="flex min-w-0 gap-3 rounded-lg border border-slate-200 bg-white p-3">
      <div className="mt-0.5 rounded-md bg-slate-100 p-2 text-slate-600">
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <div className="mt-0.5 break-words text-sm font-medium text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function optionalText(value: string): string | null {
  const normalized = value.trim();
  return normalized || null;
}

function optionalNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function StoreDetailsView({ profile }: StoreDetailsViewProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<StoreDraft>(() => draftFromProfile(profile));

  useEffect(() => {
    if (!editing) setDraft(draftFromProfile(profile));
  }, [editing, profile]);

  const formatsQuery = useQuery<StoreFormatOption[]>({
    queryKey: ['/api/store-formats', profile.retailerId, 'store-details'],
    queryFn: async () => {
      const params = profile.retailerId != null ? `?retailerId=${profile.retailerId}` : '';
      const response = await apiFetch(`/api/store-formats${params}`);
      if (!response.ok) throw new Error('Failed to load store formats');
      return response.json();
    },
    enabled: editing,
    staleTime: 60_000,
  });

  const selectedFormatName = useMemo(() => {
    if (profile.storeDetails.formatId == null) return profile.storeFormat;
    return formatsQuery.data?.find((format) => format.id === profile.storeDetails.formatId)?.name
      ?? profile.storeFormat;
  }, [formatsQuery.data, profile.storeDetails.formatId, profile.storeFormat]);

  const operatorsByFormat = useMemo(() => {
    const result = new Map<number, string[]>();
    for (const assignment of profile.formatOperatorAssignments ?? []) {
      const names = result.get(assignment.mediaFormatId) ?? [];
      if (!names.includes(assignment.operatorName)) names.push(assignment.operatorName);
      result.set(assignment.mediaFormatId, names);
    }
    return result;
  }, [profile.formatOperatorAssignments]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!profile.storeId) throw new Error('This profile is not linked to a live store');
      if (!draft.name.trim() || !draft.address.trim()) {
        throw new Error('Store name and address are required');
      }
      const response = await apiRequest(`/api/stores/${profile.storeId}`, 'PATCH', {
        name: draft.name.trim(),
        address: draft.address.trim(),
        phone: optionalText(draft.phone),
        managerName: optionalText(draft.managerName),
        retailerIdentifier: optionalText(draft.retailerIdentifier),
        region: optionalText(draft.region),
        formatId: draft.formatId === 'none' ? null : Number(draft.formatId),
        latitude: optionalNumber(draft.latitude),
        longitude: optionalNumber(draft.longitude),
        hasESL: draft.hasESL === 'true',
        notes: optionalText(draft.notes),
      });
      if (!response.ok) throw new Error('Failed to update store details');
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/api/stores', profile.id, 'detail'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/stores', 'store-profiles'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', profile.id, 'context'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/store-profiles/summary'] }),
      ]);
      setEditing(false);
      toast({ title: 'Store details updated' });
    },
    onError: (error) => {
      toast({
        title: 'Could not update store details',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const coordinates = profile.coordinates
    ? `${profile.coordinates.lat}, ${profile.coordinates.lng}`
    : 'Not recorded';

  return (
    <div className="space-y-4">
      <Card className="border-blue-200 bg-blue-50/30">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-slate-950">Store record</h2>
              <Badge variant="outline" className="border-blue-200 bg-white text-blue-800">
                Live REACTOR data
              </Badge>
              {profile.storeDetails.archivedAt ? <Badge variant="destructive">Archived</Badge> : null}
            </div>
            <p className="mt-1 text-sm text-slate-600">
              The same canonical information used in Admin → Retailers → Stores.
            </p>
          </div>
          {profile.storeId ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Edit3 className="mr-1.5 h-4 w-4" aria-hidden />
              Edit store details
            </Button>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(340px,0.7fr)]">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Identity & contact</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <DetailItem icon={Hash} label="Retailer store number" value={profile.storeCode || 'Not recorded'} />
            <DetailItem icon={Building2} label="Store format" value={selectedFormatName || 'Not assigned'} />
            <DetailItem icon={MapPin} label="Region" value={profile.region === '—' ? 'Not recorded' : profile.region} />
            <DetailItem icon={UserRound} label="Store manager" value={profile.storeDetails.managerName || 'Not assigned'} />
            <DetailItem icon={Phone} label="Phone" value={profile.storeDetails.phone || 'Not recorded'} />
            <DetailItem
              icon={MonitorSmartphone}
              label="Electronic shelf labels"
              value={profile.storeDetails.hasESL ? 'Yes' : 'No'}
            />
            <div className="sm:col-span-2 lg:col-span-3">
              <DetailItem icon={MapPin} label="Address" value={profile.fullAddress || 'Not recorded'} />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <DetailItem icon={MapPin} label="Coordinates" value={coordinates} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Store notes</CardTitle>
          </CardHeader>
          <CardContent>
            {profile.storeDetails.notes ? (
              <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">{profile.storeDetails.notes}</p>
            ) : (
              <p className="text-sm text-slate-500">No store-level notes have been recorded.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <CardTitle className="text-base">Media formats & capacity</CardTitle>
              <p className="mt-1 text-sm text-slate-500">
                Current store requirements and their format-level operator assignments.
              </p>
            </div>
            <div className="flex gap-2">
              <Badge variant="secondary">{profile.totalMediaLocations} formats</Badge>
              <Badge variant="secondary">{profile.totalMediaCapacity} total units</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {profile.mediaLocations.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-slate-500">
              No media formats are configured for this store.
            </div>
          ) : (
            <div className="divide-y overflow-hidden rounded-lg border">
              {profile.mediaLocations.map((media) => {
                const operators = media.mediaFormatId != null
                  ? operatorsByFormat.get(media.mediaFormatId) ?? []
                  : [];
                return (
                  <div
                    key={media.id}
                    className="grid grid-cols-1 gap-2 bg-white px-4 py-3 sm:grid-cols-[minmax(0,1fr)_110px_minmax(180px,0.7fr)] sm:items-center"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">{media.formatLabel}</p>
                      {media.locationLabel && media.locationLabel !== `Store capacity — ${media.formatLabel}` ? (
                        <p className="mt-0.5 truncate text-xs text-slate-500">{media.locationLabel}</p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-slate-700">
                      <Package className="h-4 w-4 text-slate-400" aria-hidden />
                      <span>{media.capacity} units</span>
                    </div>
                    <div className="text-sm">
                      {operators.length > 0 ? (
                        <span className="text-slate-700">{operators.join(', ')}</span>
                      ) : (
                        <span className="text-amber-700">No operator assigned</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>Edit store details</DialogTitle>
            <DialogDescription>
              Updates are saved to the canonical store record and will also appear in Admin → Retailers → Stores.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="profile-store-name">Store name</Label>
              <Input
                id="profile-store-name"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-store-number">Retailer store number</Label>
              <Input
                id="profile-store-number"
                value={draft.retailerIdentifier}
                onChange={(event) => setDraft({ ...draft, retailerIdentifier: event.target.value })}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="profile-store-address">Address</Label>
              <Textarea
                id="profile-store-address"
                value={draft.address}
                onChange={(event) => setDraft({ ...draft, address: event.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-store-region">Region</Label>
              <Input
                id="profile-store-region"
                value={draft.region}
                onChange={(event) => setDraft({ ...draft, region: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Store format</Label>
              <Select
                value={draft.formatId}
                onValueChange={(value) => setDraft({ ...draft, formatId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a store format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not assigned</SelectItem>
                  {(formatsQuery.data ?? []).map((format) => (
                    <SelectItem key={format.id} value={String(format.id)}>
                      {format.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-store-manager">Store manager</Label>
              <Input
                id="profile-store-manager"
                value={draft.managerName}
                onChange={(event) => setDraft({ ...draft, managerName: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-store-phone">Phone</Label>
              <Input
                id="profile-store-phone"
                value={draft.phone}
                onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-store-latitude">Latitude</Label>
              <Input
                id="profile-store-latitude"
                inputMode="decimal"
                value={draft.latitude}
                onChange={(event) => setDraft({ ...draft, latitude: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-store-longitude">Longitude</Label>
              <Input
                id="profile-store-longitude"
                inputMode="decimal"
                value={draft.longitude}
                onChange={(event) => setDraft({ ...draft, longitude: event.target.value })}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Electronic shelf labels</Label>
              <Select
                value={draft.hasESL}
                onValueChange={(value: 'true' | 'false') => setDraft({ ...draft, hasESL: value })}
              >
                <SelectTrigger className="sm:max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="profile-store-notes">Store notes</Label>
              <Textarea
                id="profile-store-notes"
                value={draft.notes}
                onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={updateMutation.isPending}
              onClick={() => updateMutation.mutate()}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save store details'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
